<script setup lang="ts">
/**
 * The principal risks, one card each.
 *
 * A card rather than a row, for the reason the IRO register uses one, and a
 * stronger version of it: the description and the controls are paragraphs,
 * and a single-line input in a rail this narrow shows about six words of a
 * paragraph. They are text areas here because they are paragraphs on the page.
 *
 * The family is typed rather than picked from a list. The three kinds ESRS
 * distinguishes are fixed by the standard; the families a company sorts its
 * risks into are its own, and one report has "Risques liés à la sûreté" where
 * the next has nothing of the kind. The severity is a closed list, because a
 * fourth level is a disagreement about the scale rather than a new level.
 */
import { useI18n } from 'vue-i18n'

import { newRiskEntry, type RiskEntry, type RisksSection } from '@/domain/section'
import { RISK_LEVELS } from '@/sections/risks'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: RisksSection }>()
const emit = defineEmits<{ change: [section: RisksSection] }>()

function set(changes: Partial<RisksSection>): void {
  emit('change', { ...props.section, ...changes })
}

function setEntry(id: string, changes: Partial<RiskEntry>): void {
  set({
    entries: props.section.entries.map((entry) =>
      entry.id === id ? { ...entry, ...changes } : entry,
    ),
  })
}

/**
 * A new risk joins the family of the one above it.
 *
 * Risks are written a family at a time, so the likeliest next risk belongs
 * with the last one, and retyping the family on every card is how one report
 * ends up with "Risques opérationnels" and "Risques operationnels" as two
 * families.
 */
function add(): void {
  const last = props.section.entries.at(-1)
  set({
    entries: [...props.section.entries, { ...newRiskEntry(), category: last?.category ?? '' }],
  })
}

function remove(id: string): void {
  set({ entries: props.section.entries.filter((entry) => entry.id !== id) })
}

function typed(event: Event): string {
  return (event.target as HTMLInputElement | HTMLTextAreaElement).value
}
</script>

<template>
  <div class="fields">
    <div class="entries">
      <div v-for="entry in section.entries" :key="entry.id" class="entry">
        <div class="entry__head">
          <input
            class="b-input entry__title"
            :value="entry.title"
            :placeholder="t('inspector.risks.titlePlaceholder')"
            :aria-label="t('inspector.risks.title')"
            @input="setEntry(entry.id, { title: typed($event) })"
          />
          <button
            type="button"
            class="b-icon-btn b-btn--danger"
            :aria-label="t('inspector.risks.removeEntry')"
            @click="remove(entry.id)"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4l-8 8"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <div class="entry__row">
          <input
            class="b-input"
            :value="entry.category"
            :placeholder="t('inspector.risks.categoryPlaceholder')"
            :aria-label="t('inspector.risks.category')"
            @input="setEntry(entry.id, { category: typed($event) })"
          />

          <select
            class="b-select"
            :value="entry.level"
            :aria-label="t('inspector.risks.level')"
            @change="setEntry(entry.id, { level: typed($event) as RiskEntry['level'] })"
          >
            <option v-for="option in RISK_LEVELS" :key="option" :value="option">
              {{ t(`inspector.risks.levels.${option}`) }}
            </option>
          </select>
        </div>

        <textarea
          class="b-textarea"
          rows="3"
          :value="entry.description"
          :placeholder="t('inspector.risks.descriptionPlaceholder')"
          :aria-label="t('inspector.risks.description')"
          @input="setEntry(entry.id, { description: typed($event) })"
        />

        <textarea
          class="b-textarea"
          rows="3"
          :value="entry.management"
          :placeholder="t('inspector.risks.managementPlaceholder')"
          :aria-label="t('inspector.risks.management')"
          @input="setEntry(entry.id, { management: typed($event) })"
        />
      </div>
    </div>

    <button type="button" class="b-btn b-btn--outline" @click="add">
      {{ t('inspector.risks.addEntry') }}
    </button>

    <InspectorField :label="t('inspector.risks.caption')">
      <input class="b-input" :value="section.caption" @input="set({ caption: typed($event) })" />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.entries {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.entry {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-4);
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline);
}

.entry__head {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}

.entry__title {
  flex: 1;
  min-width: 0;
  font-weight: 600;
}

/* The family and the severity are read together: where it sits, and how much
   it matters. */
.entry__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 7.5rem;
  gap: var(--sp-2);
}

.entry__row .b-select {
  min-width: 0;
  padding-left: var(--sp-4);
  font-size: var(--text-sm);
}
</style>
